import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useLocalStorageState } from 'ahooks';
import { HexColorInput, HexColorPicker } from 'react-colorful';

import { Icon } from '../icon';
import { Divider } from '../divider';
import { Collapse } from '../collapse';
import { ColorSet } from '../color-set';
import { ColorIndicator, ColorIndicatorProps } from '../color-indicator';
import { Popover, type PopoverProps } from '../popover';

import { recommendedColorSet, tagDefaultColor } from '../../constants/ui';

import styles from './color-picker.module.scss';

const defaultExpanded = { defaultValue: true };

export const ColorPicker = (props: ColorPickerProps) => {
  const { color, onChange, indicatorSize, showRecommended = true, ...rest } = props;

  const [value, setValue] = useState(tagDefaultColor);

  const [recentExpanded, setRecentExpanded] = useLocalStorageState('cp-recent-expanded', defaultExpanded);
  const [customExpanded, setCustomExpanded] = useLocalStorageState('cp-custom-expanded', defaultExpanded);
  const [recommendedExpanded, setRecommendedExpanded] = useLocalStorageState('cp-recommended', defaultExpanded);
  const [recentColors, setRecentColors] = useLocalStorageState<string[]>('cp-recent-color', {
    defaultValue: [],
  });

  useEffect(() => {
    if (color && color !== value) {
      setValue(color);
    }
  }, [color]);

  return (
    <Popover
      withOverlay
      offset={[0, 6]}
      className={styles.colorPicker}
      onClose={() => {
        if (color !== value) {
          onChange?.(value);
          setRecentColors((val) => [...new Set([value, ...(val as string[])])].slice(0, 10));
        }
      }}
      {...rest}
      content={
        <>
          <HexColorPicker color={value} onChange={setValue} />
          <div className={styles.cpContentHolder}>
            {showRecommended && (
              <>
                <Collapse label="Recommended" open={!!recommendedExpanded} onChange={(state) => setRecommendedExpanded(state)}>
                  <ColorSet gap={10} value={value} onSelect={setValue} data={recommendedColorSet} />
                </Collapse>
                <Divider orientation="horizontal" size="100%" />
              </>
            )}
            <Collapse label="Recent" open={!!recentExpanded} onChange={(state) => setRecentExpanded(state)}>
              {!recentColors!.length ? (
                <span className={styles.emptyPreset}>Empty</span>
              ) : (
                <ColorSet gap={10} value={value} onSelect={setValue} data={recentColors!} />
              )}
            </Collapse>
            <Divider orientation="horizontal" size="100%" />
            <Collapse label="Custom" open={!!customExpanded} onChange={(state) => setCustomExpanded(state)}>
              <ul className={styles.cpInputRow}>
                <li>
                  <label htmlFor="cp-input">HEX</label>
                  <div className={styles.cpInputHolder}>
                    <span>#</span>
                    <HexColorInput id="cp-input" data-testid="hex-input" color={value} onChange={setValue} />
                  </div>
                </li>
                <li>
                  <ColorIndicator color={value} />
                  <Icon
                    size={18}
                    type="color-picker"
                    className={clsx(styles.eyeDropper, {
                      // @ts-ignore
                      [styles.disabled]: !window.EyeDropper,
                    })}
                    onClick={() => {
                      // @ts-ignore
                      const eyeDropper = new EyeDropper();
                      const abortController = new AbortController();

                      eyeDropper
                        .open({ signal: abortController.signal })
                        .then((result: any) => {
                          setValue(result.sRGBHex);
                          abortController.abort();
                        })
                        .catch(() => {
                          abortController.abort();
                        });
                    }}
                  />
                </li>
              </ul>
            </Collapse>
          </div>
        </>
      }
    >
      <ColorIndicator size={indicatorSize} data-testid="color-picker-trigger" color={value} />
    </Popover>
  );
};

export interface ColorPickerProps extends PopoverProps {
  color?: string;
  showRecommended?: boolean;
  onChange?: (value: string) => void;
  indicatorSize?: ColorIndicatorProps['size'];
}
